import { useEffect, useState, type FormEvent } from 'react';
import { Lock } from 'lucide-react';
import {
  canonicalReferralCode,
  ERROR_CODES,
  REFERRAL_CODE_LIMITS,
  type PanelReferralCodeDto,
} from '@icebreaker/shared';
import {
  Button,
  DateTimePicker,
  Field,
  Input,
  Modal,
  Textarea,
  useFormErrors,
} from '@icebreaker/ui';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  failureKind,
  fieldErrorPaths,
  useTodayInIstanbul,
} from '@/features/organizations/ui/dialog-parts';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  EMPTY_REFERRAL_FORM,
  codePreview,
  formFromCode,
  toCreateBody,
  toUpdateBody,
  validateReferralForm,
  type ReferralFormField,
  type ReferralFormValues,
} from '../model/referral-code-view';
import {
  createReferralCode,
  fetchReferralCodes,
  referralWriteReset,
  updateReferralCode,
} from '../store/referral-codes.slice';
import styles from './referral-codes.module.css';

const FORM_ID = 'referral-code-form';

/** Sunucunun alan yolu → diyaloğun alanı (ad ve hediye yalnız oluşturmada gelir). */
const SERVER_PATHS: Readonly<Record<string, ReferralFormField>> = {
  name: 'code',
  giftCredits: 'gift',
  usageCap: 'cap',
  endDay: 'endDay',
};

type DialogProps =
  | { readonly mode: 'create'; readonly code?: undefined; readonly onClose: () => void }
  | { readonly mode: 'edit'; readonly code: PanelReferralCodeDto; readonly onClose: () => void };

/**
 * KOD OLUŞTUR / DÜZENLE (P7 diyaloğu, sekiz hâl).
 *
 * Doğrulama alan odaktan çıkınca ve gönderirken çalışır; "kod zaten var" yalnız
 * sunucudan gelir (409 `REFERRAL_CODE_NAME_TAKEN`) ve kod alanının altına
 * yazılır. Düzenlemede kod ve hediye KİLİTLİDİR ve gövdeye girmez (sunucu
 * bilinmeyen alanı reddeder); silme yoktur, kod Aktif anahtarıyla kapatılır.
 *
 * Gönderim sürerken iki düğme ve bütün alanlar kapalı, X ve Esc kilitli: çift
 * basış ikinci istek üretmez. Sunucu hatasında girdiler korunur. Sonuç
 * bilinmiyorsa (yanıt yok / 5xx) kesin bir "kaydedilmedi" cümlesi kurulmaz,
 * liste yeniden çekilir ve personel listeyi kontrol etmeye yönlendirilir.
 */
export function ReferralCodeDialog(props: DialogProps) {
  const { mode, onClose } = props;
  const editing = mode === 'edit' ? props.code : null;
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const writeKey = mode === 'create' ? 'create' : 'update';
  const write = useAppSelector((root: RootState) => root.panelReferral.writes[writeKey]);
  const today = useTodayInIstanbul();
  const [values, setValues] = useState<ReferralFormValues>(() =>
    editing ? formFromCode(editing) : EMPTY_REFERRAL_FORM,
  );
  const [touched, setTouched] = useState<ReadonlySet<ReferralFormField>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  // "Kod zaten var" yalnız gönderilen ad için doğrudur: ad değişince cümle düşer.
  const [sentName, setSentName] = useState<string | null>(null);

  useEffect(() => {
    dispatch(referralWriteReset(writeKey));
    return () => {
      dispatch(referralWriteReset(writeKey));
    };
  }, [dispatch, writeKey]);

  useEffect(() => {
    if (write.phase === 'succeeded') onClose();
  }, [onClose, write.phase]);

  const busy = write.phase === 'loading';
  const outcome = failureKind(write.phase, write.error);
  const nameTaken =
    write.error?.code === ERROR_CODES.REFERRAL_CODE_NAME_TAKEN &&
    sentName !== null &&
    canonicalReferralCode(values.code) === sentName;
  const nameConflict = write.error?.code === ERROR_CODES.REFERRAL_CODE_NAME_TAKEN;
  const rejected = outcome === 'rejected' && !nameConflict;
  const unknown = outcome === 'unknown';
  const serverPaths = fieldErrorPaths(write.error);
  const serverForm = useFormErrors(write.error, ['startDay', 'note']);

  const clientErrors = validateReferralForm(values, mode);
  const errorFor = (field: ReferralFormField): string | undefined => {
    const key = clientErrors[field];
    if (key && (submitted || touched.has(field))) return t.t(key);
    if (field === 'code' && nameTaken) return t.t('admin.referralCodes.dialog.codeTaken');
    const serverPath = Object.keys(SERVER_PATHS).find((path) => SERVER_PATHS[path] === field);
    if (serverPath && serverPaths.has(serverPath)) {
      return t.t(
        field === 'code'
          ? 'admin.referralCodes.dialog.codeFormat'
          : field === 'gift'
            ? 'admin.referralCodes.dialog.giftRange'
            : field === 'cap'
              ? 'admin.referralCodes.dialog.capInvalid'
              : 'admin.referralCodes.dialog.endBeforeStart',
      );
    }
    return undefined;
  };
  const touch = (field: ReferralFormField) =>
    setTouched((previous) => (previous.has(field) ? previous : new Set(previous).add(field)));
  const set = <K extends keyof ReferralFormValues>(key: K, value: ReferralFormValues[K]) =>
    setValues((previous) => ({ ...previous, [key]: value }));

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (busy) return;
    setSubmitted(true);
    if (Object.keys(validateReferralForm(values, mode)).length > 0) return;
    if (editing) {
      dispatch(updateReferralCode.request({ id: editing.id, body: toUpdateBody(values) }));
      return;
    }
    const body = toCreateBody(values);
    setSentName(body.name);
    dispatch(createReferralCode.request(body));
  };

  const refreshList = () => {
    dispatch(fetchReferralCodes.request());
    onClose();
  };

  const codeError = errorFor('code');
  const giftError = errorFor('gift');
  const capError = errorFor('cap');
  const endError = errorFor('endDay');
  const startError = serverForm.at('startDay');
  const noteError = serverForm.at('note');
  const preview = editing ? null : codePreview(values.code);

  return (
    <Modal
      open
      title={t.t(
        editing ? 'admin.referralCodes.dialog.editTitle' : 'admin.referralCodes.dialog.createTitle',
      )}
      closeLabel={t.t('admin.referralCodes.dialog.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <DialogFooter audit={t.t('admin.referralCodes.dialog.audit')}>
          {unknown ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                {t.t('admin.referralCodes.dialog.close')}
              </Button>
              <Button variant="primary" onClick={refreshList}>
                {t.t('admin.referralCodes.dialog.refresh')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" disabled={busy} onClick={onClose}>
                {t.t('admin.referralCodes.dialog.cancel')}
              </Button>
              <Button
                variant="primary"
                type="submit"
                form={FORM_ID}
                loading={busy}
                loadingLabel={t.t(
                  editing
                    ? 'admin.referralCodes.dialog.saving'
                    : 'admin.referralCodes.dialog.creating',
                )}
              >
                {rejected
                  ? t.t('admin.referralCodes.retry')
                  : t.t(
                      editing
                        ? 'admin.referralCodes.dialog.save'
                        : 'admin.referralCodes.dialog.createTitle',
                    )}
              </Button>
            </>
          )}
        </DialogFooter>
      }
    >
      {editing ? (
        <DialogSub>
          {editing.usageCount > 0
            ? t.t('admin.referralCodes.dialog.usedBy', { count: editing.usageCount })
            : t.t('admin.referralCodes.dialog.notUsed')}
        </DialogSub>
      ) : null}
      <form id={FORM_ID} className={styles.formGrid} onSubmit={submit} noValidate>
        {unknown ? (
          <p
            className={[styles.bannerWarn, styles.span2].join(' ')}
            role="alert"
            data-outcome="unknown"
          >
            {t.t('admin.write.unknown')} {t.t('admin.write.reloadedReferralCodes')}
          </p>
        ) : rejected ? (
          <div className={styles.span2}>
            <ServerBanner>
              {t.t(
                editing
                  ? 'admin.referralCodes.dialog.updateError'
                  : 'admin.referralCodes.dialog.createError',
              )}
            </ServerBanner>
          </div>
        ) : null}

        <div className={styles.span2}>
          <Field
            id="referral-code"
            label={<Label text={t.t('admin.referralCodes.dialog.code')} required />}
            hint={codeError || editing ? undefined : t.t('admin.referralCodes.dialog.codeHint')}
            error={codeError}
          >
            {editing ? (
              <Locked id="referral-code" value={editing.name} mono />
            ) : (
              <>
                <Input
                  id="referral-code"
                  className={styles.codeInput}
                  value={values.code}
                  disabled={busy}
                  invalid={Boolean(codeError)}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  maxLength={REFERRAL_CODE_LIMITS.nameMax + 8}
                  placeholder={t.t('admin.referralCodes.dialog.codePlaceholder')}
                  onChange={(event) => set('code', event.target.value)}
                  onBlur={() => touch('code')}
                />
                {preview ? (
                  <p className={styles.preview}>
                    {t.t('admin.referralCodes.dialog.savedAs')}{' '}
                    <b className={styles.previewValue}>{preview}</b>
                  </p>
                ) : null}
              </>
            )}
          </Field>
        </div>

        <Field
          id="referral-gift"
          label={<Label text={t.t('admin.referralCodes.dialog.gift')} required />}
          hint={giftError || editing ? undefined : t.t('admin.referralCodes.dialog.giftHint')}
          error={giftError}
        >
          {editing ? (
            <Locked id="referral-gift" value={String(editing.giftCredits)} />
          ) : (
            <Input
              id="referral-gift"
              inputMode="numeric"
              value={values.gift}
              disabled={busy}
              invalid={Boolean(giftError)}
              placeholder={t.t('admin.referralCodes.dialog.giftPlaceholder')}
              onChange={(event) => set('gift', event.target.value)}
              onBlur={() => touch('gift')}
            />
          )}
        </Field>
        <Field
          id="referral-cap"
          label={<Label text={t.t('admin.referralCodes.dialog.cap')} />}
          hint={capError ? undefined : t.t('admin.referralCodes.dialog.capHint')}
          error={capError}
        >
          <Input
            id="referral-cap"
            inputMode="numeric"
            value={values.cap}
            disabled={busy}
            invalid={Boolean(capError)}
            placeholder={t.t('admin.referralCodes.dialog.capPlaceholder')}
            onChange={(event) => set('cap', event.target.value)}
            onBlur={() => touch('cap')}
          />
        </Field>

        <Field
          id="referral-start"
          label={<Label text={t.t('admin.referralCodes.dialog.start')} />}
          error={startError}
        >
          <DateTimePicker
            id="referral-start"
            mode="date"
            value={values.startDay}
            today={today}
            invalid={Boolean(startError)}
            disabled={busy}
            clearable
            onChange={(day) => set('startDay', day)}
          />
        </Field>
        <Field
          id="referral-end"
          label={<Label text={t.t('admin.referralCodes.dialog.end')} />}
          error={endError}
        >
          <DateTimePicker
            id="referral-end"
            mode="date"
            value={values.endDay}
            min={values.startDay || undefined}
            today={today}
            invalid={Boolean(endError)}
            disabled={busy}
            clearable
            onChange={(day) => {
              set('endDay', day);
              touch('endDay');
            }}
          />
        </Field>

        <div className={styles.span2}>
          <Field
            id="referral-note"
            label={<Label text={t.t('admin.referralCodes.dialog.note')} />}
            hint={noteError ? undefined : t.t('admin.referralCodes.dialog.noteHint')}
            error={noteError}
          >
            <Textarea
              id="referral-note"
              rows={2}
              value={values.note}
              disabled={busy}
              invalid={Boolean(noteError)}
              maxLength={REFERRAL_CODE_LIMITS.note}
              placeholder={t.t('admin.referralCodes.dialog.notePlaceholder')}
              onChange={(event) => set('note', event.target.value)}
            />
          </Field>
        </div>

        {editing ? (
          <p className={[styles.lockNote, styles.span2].join(' ')}>
            {t.t('admin.referralCodes.dialog.lockNote')}
          </p>
        ) : null}
        <div className={styles.span2}>
          <InfoBlock tone="info">{t.t('admin.referralCodes.dialog.giftInfo')}</InfoBlock>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={values.active}
          aria-labelledby="referral-active-title"
          aria-describedby="referral-active-hint"
          disabled={busy}
          className={[styles.switchRow, styles.span2, values.active ? styles.switchOn : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => set('active', !values.active)}
        >
          <span className={styles.switchTrack} aria-hidden="true">
            <span className={styles.switchThumb} />
          </span>
          <span className={styles.switchText}>
            <span id="referral-active-title" className={styles.switchTitle}>
              {t.t('admin.referralCodes.dialog.active')}
            </span>
            <span id="referral-active-hint" className={styles.switchHint}>
              {t.t('admin.referralCodes.dialog.activeHint')}
            </span>
          </span>
        </button>
      </form>
    </Modal>
  );
}

function Label({ text, required = false }: { text: string; required?: boolean }) {
  const t = useTranslation();
  return (
    <>
      {text}{' '}
      <span className={styles.labelNote}>
        {t.t(
          required ? 'admin.referralCodes.dialog.required' : 'admin.referralCodes.dialog.optional',
        )}
      </span>
    </>
  );
}

/** Düzenlemede değişmeyen alan: okunur, odak alır, değiştirilemez (kilit simgeli). */
function Locked({ id, value, mono = false }: { id: string; value: string; mono?: boolean }) {
  const t = useTranslation();
  return (
    <span className={styles.locked}>
      <Input
        id={id}
        value={value}
        readOnly
        aria-readonly
        className={mono ? styles.codeInput : undefined}
      />
      <Lock
        size={15}
        role="img"
        className={styles.lockIcon}
        aria-label={t.t('admin.referralCodes.dialog.locked')}
      />
    </span>
  );
}
